/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { createApi } from "@reduxjs/toolkit/query/react"
import { InfiniteData } from "@reduxjs/toolkit/query"
import {
  FoodmaxapipbListSellingTimesResponse,
  FoodmaxapipbGetModifierGroupDetailsByModifierGroupIDsResponse,
  FoodmaxapipbGetItemDetailsByItemIDsResponse,
  FoodmaxapipbSearchItemsResponse,
  FoodmaxapipbSearchMenusForMexResponse,
  FoodmaxapipbMartItemMenuDisplayV2Response,
  FoodmaxapipbListAttributesResponse,
  FoodmaxapipbUpdateItemAvailableStatusV2Response,
} from "generated/foodmaxapi.type"
import { FetchStoreListResponse } from "types/api/menuFetchStoreList.type"
import { FetchItemsWithoutPhotoCountResponse } from "types/api/menuFetchItemsWithoutPhotoCount.type"
import { FetchMerchantAggregateResponse } from "types/api"
import { axiosBaseQuery, createRequestHeaders } from "./apiClient"
import {
  apiReducerPaths,
  CACHE_QUERY_TAGS,
  endpoints,
  dpApiEndpoints,
  dpApiMerchantUrl,
} from "./constants"
import {
  ListSellingTimesRequest,
  GetModifierGroupsByIDsRequest,
  GetItemDetailsByItemIDsRequest,
  SearchItemsRequest,
  SearchMenusForMexRequest,
  MartItemMenuDisplayV2Request,
  UploadFileRequest,
  UploadFileResponse,
  ListAttributesRequest,
  DeleteItemV2Request,
  UpdateItemsAvailableV1Request,
  UpdateItemsSellingTimeRequest,
  SearchOutletsRequest,
  FetchMerchantAggregateRequest,
  HttpMethod,
} from "./types"

// Maximum number of itemIDs that can be sent in a single GetItemDetailsByItemIDs request
export const GET_ITEM_DETAILS_BATCH_SIZE = 100

// Default page size for list attributes
export const DEFAULT_PAGE_SIZE_LIST_ATTRIBUTES = 100

// Base query for delvplatformapi endpoints
const dpApiBaseQuery = axiosBaseQuery(dpApiMerchantUrl)

export const menuApi = createApi({
  reducerPath: apiReducerPaths.menuApi,
  baseQuery: axiosBaseQuery(),
  refetchOnMountOrArgChange: 300,
  tagTypes: [
    CACHE_QUERY_TAGS.listSellingTimes,
    CACHE_QUERY_TAGS.searchMenusForMex,
    CACHE_QUERY_TAGS.martItemMenuDisplayV2,
    // MenuV3 tags
    CACHE_QUERY_TAGS.menuV3Items,
    CACHE_QUERY_TAGS.menuV3Hierarchies,
    CACHE_QUERY_TAGS.menuV3ModifierGroups,
  ],
  endpoints: builder => ({
    listSellingTimes: builder.query<
      FoodmaxapipbListSellingTimesResponse,
      ListSellingTimesRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        isActive = true,
        page,
        pageSize,
        name,
      }) => ({
        url: endpoints.listSellingTimes,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          merchantID,
          isActive,
          page,
          pageSize,
          name,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.listSellingTimes],
    }),
    getModifierGroupsByIDs: builder.query<
      FoodmaxapipbGetModifierGroupDetailsByModifierGroupIDsResponse,
      GetModifierGroupsByIDsRequest
    >({
      query: ({ merchantID, merchantGroupID, modifierGroupIDs }) => ({
        url: endpoints.modifierGroupsDetails,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          modifierGroupIDs,
        },
      }),
    }),
    getItemDetailsByItemIDs: builder.infiniteQuery<
      FoodmaxapipbGetItemDetailsByItemIDsResponse,
      GetItemDetailsByItemIDsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (
          _lastPage,
          _allPages,
          lastPageParam,
          _allPageParams,
          queryArg,
        ) => {
          // Calculate  items loaded after this page
          const expectedLoadedCount =
            (lastPageParam + 1) * GET_ITEM_DETAILS_BATCH_SIZE
          const totalItemIDs = queryArg.itemIDs?.length || 0

          // Continue fetching if we haven't reached all itemIDs yet
          return expectedLoadedCount < totalItemIDs
            ? lastPageParam + 1
            : undefined
        },
      },
      query: ({ queryArg, pageParam }) => {
        const itemIDs = queryArg.itemIDs || []
        const batchIndex = pageParam as number
        const startIndex = batchIndex * GET_ITEM_DETAILS_BATCH_SIZE
        const endIndex = startIndex + GET_ITEM_DETAILS_BATCH_SIZE
        const itemIDsBatch = itemIDs.slice(startIndex, endIndex)

        return {
          url: endpoints.itemDetailsByItemIDs,
          headers: createRequestHeaders({
            merchantID: queryArg.merchantID,
            merchantGroupID: queryArg.merchantGroupID,
          }),
          params: {
            itemIDs: itemIDsBatch,
            needStock: queryArg.needStock,
            needModifierGroups: queryArg.needModifierGroups,
          },
        }
      },
    }),
    searchItems: builder.query<
      FoodmaxapipbSearchItemsResponse,
      SearchItemsRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        searchKey,
        needClassItemsInfo = true,
        needCategoryInfo = true,
        needStock = true,
      }) => ({
        url: endpoints.searchItems,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          searchKey,
          needClassItemsInfo,
          needCategoryInfo,
          needStock,
        },
      }),
    }),
    /**
     * Search menus for merchant
     * GET /v2/search-menu
     */
    searchMenusForMex: builder.query<
      FoodmaxapipbSearchMenusForMexResponse,
      SearchMenusForMexRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        keyword,
        entityType,
        departmentID,
      }) => ({
        url: endpoints.searchMenusForMex,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          keyword,
          entityType,
          departmentID,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.searchMenusForMex],
    }),
    /**
     * Fetch catalogue (mart-menu)
     * GET /v2/mart-menu
     */
    martItemMenuDisplayV2: builder.query<
      FoodmaxapipbMartItemMenuDisplayV2Response,
      MartItemMenuDisplayV2Request
    >({
      query: ({ merchantID, merchantGroupID, hideModifierGroup = true }) => ({
        url: endpoints.martItemMenuDisplayV2,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          hideModifierGroup,
        },
      }),
      providesTags: [CACHE_QUERY_TAGS.martItemMenuDisplayV2],
      keepUnusedDataFor: Infinity,
    }),
    /**
     * Upload a product/item photo
     * POST /v2/upload-file
     * Returns the CDN URL of the uploaded image.
     */
    uploadFile: builder.mutation<UploadFileResponse, UploadFileRequest>({
      query: ({ merchantID, file, category }) => ({
        url: endpoints.uploadFile,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID }),
        data: { file, category },
      }),
    }),
    /**
     * List attributes
     * GET /v1/attributes
     */
    listAttributes: builder.query<
      FoodmaxapipbListAttributesResponse,
      ListAttributesRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        attributeClusterIDs,
        page,
        name,
        locale,
      }) => ({
        url: endpoints.listAttributes,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        params: {
          attributeClusterIDs,
          name,
          locale,
          page,
          pageSize: DEFAULT_PAGE_SIZE_LIST_ATTRIBUTES,
          status: "Enabled",
        },
      }),
    }),
    /**
     * bulk update item availability
     * PUT /v1/items/available-status
     */
    updateItemsAvailableV1: builder.mutation<
      FoodmaxapipbUpdateItemAvailableStatusV2Response,
      UpdateItemsAvailableV1Request
    >({
      query: ({
        merchantID,
        merchantGroupID,
        availableStatus,
        itemIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
      }) => ({
        url: endpoints.updateItemsAvailableV1,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          availableStatus,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
        },
      }),
      invalidatesTags: [
        CACHE_QUERY_TAGS.menuV3Items,
        CACHE_QUERY_TAGS.menuV3Hierarchies,
      ],
    }),
    /**
     * Bulk update items selling time
     * PUT /v2/items/sellingTime
     */
    updateItemsSellingTime: builder.mutation<
      void,
      UpdateItemsSellingTimeRequest
    >({
      query: ({
        merchantID,
        merchantGroupID,
        itemIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
        sellingTimeID,
      }) => ({
        url: endpoints.updateItemsSellingTimeV2,
        method: HttpMethod.PUT,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
          item: { sellingTimeID },
        },
      }),
      invalidatesTags: [
        CACHE_QUERY_TAGS.menuV3Items,
        CACHE_QUERY_TAGS.menuV3Hierarchies,
      ],
    }),
    /**
     * bulk delete items
     * DELETE /v2/items/bulk
     * itemID path param is "bulk"; selection scope is sent in the request body.
     */
    deleteItemV2: builder.mutation<void, DeleteItemV2Request>({
      query: ({
        merchantID,
        merchantGroupID,
        itemIDs,
        categoryIDs,
        departmentIDs,
        subDepartmentIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
      }) => ({
        url: endpoints.deleteItemV2,
        method: HttpMethod.DELETE,
        headers: createRequestHeaders({ merchantID, merchantGroupID }),
        data: {
          itemIDs,
          categoryIDs,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
        },
      }),
      invalidatesTags: [
        CACHE_QUERY_TAGS.menuV3Items,
        CACHE_QUERY_TAGS.menuV3Hierarchies,
        CACHE_QUERY_TAGS.menuV3ModifierGroups,
      ],
    }),
    /**
     * Fetch count of items without photos across all stores in the merchant group.
     * GET /delvplatformapi/merchant/v1/merchant-group/store/merchants-items-without-photo-count
     */
    fetchItemsWithoutPhotoCount: builder.query<
      FetchItemsWithoutPhotoCountResponse,
      void
    >({
      queryFn: async (_arg, queryApi, extraOptions) => {
        const result = await dpApiBaseQuery(
          { url: dpApiEndpoints.fetchItemsWithoutPhotoCount },
          queryApi,
          extraOptions,
        )
        if (result.error) return { ...result }
        return {
          ...result,
          data: result.data as FetchItemsWithoutPhotoCountResponse,
        }
      },
    }),
    /**
     * Search outlets/stores with infinite (offset-based) pagination.
     * `offset` is managed as pageParam — do NOT include it in the query arg.
     * GET /delvplatformapi/merchant/v1/merchant-group/store/search
     */
    searchOutletsPaginated: builder.infiniteQuery<
      FetchStoreListResponse,
      Omit<SearchOutletsRequest, "offset">,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
          if (!lastPage.hasMore) return undefined
          return allPages.reduce(
            (sum, page) => sum + (page.merchants?.length || 0),
            0,
          )
        },
      },
      queryFn: async ({ queryArg, pageParam }, queryApi, extraOptions) => {
        const { merchantID, merchantGroupID, ...params } = queryArg
        const result = await dpApiBaseQuery(
          {
            url: dpApiEndpoints.searchOutlets,
            headers: createRequestHeaders({ merchantID, merchantGroupID }),
            params: {
              ...params,
              offset: pageParam,
              includeInactive: true,
              cityIDs: params.city,
              businessTypes: params.businessType,
              deliverOptionsOriginal: params.deliverOption,
            },
          },
          queryApi,
          extraOptions,
        )
        if (result.error) return { ...result }
        return { ...result, data: result.data as FetchStoreListResponse }
      },
    }),
    /**
     * Fetch merchant aggregate (cities, business types, deliver options)
     * GET /v1/merchant-group/store/aggregate-by-fields
     */
    fetchMerchantAggregate: builder.query<
      FetchMerchantAggregateResponse,
      FetchMerchantAggregateRequest
    >({
      queryFn: async ({ merchantGroupID, fields }, queryApi, extraOptions) => {
        const result = await dpApiBaseQuery(
          {
            url: dpApiEndpoints.fetchMerchantAggregate,
            headers: createRequestHeaders({ merchantGroupID }),
            params: {
              fields,
              modelType: "integrated",
              includeInactive: true,
            },
          },
          queryApi,
          extraOptions,
        )
        if (result.error) return { ...result }
        return {
          ...result,
          data: result.data as FetchMerchantAggregateResponse,
        }
      },
    }),
  }),
})

export const {
  useListSellingTimesQuery,
  useGetModifierGroupsByIDsQuery,
  useGetItemDetailsByItemIDsInfiniteQuery,
  useSearchItemsQuery,
  useLazySearchItemsQuery,
  useSearchMenusForMexQuery,
  useMartItemMenuDisplayV2Query,
  useUploadFileMutation,
  useLazyListAttributesQuery,
  useDeleteItemV2Mutation,
  useUpdateItemsAvailableV1Mutation,
  useUpdateItemsSellingTimeMutation,
  useSearchOutletsPaginatedInfiniteQuery,
  useFetchItemsWithoutPhotoCountQuery,
  useFetchMerchantAggregateQuery,
} = menuApi

export function flattenInfiniteItemsByIDs(
  data:
    | InfiniteData<FoodmaxapipbGetItemDetailsByItemIDsResponse, number>
    | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.items || [])
}

export function flattenInfiniteOutlets(
  data: InfiniteData<FetchStoreListResponse, number> | undefined,
) {
  if (!data?.pages) return []
  return data.pages.flatMap(page => page.merchants || [])
}
